import { useEffect, useState } from 'react';
import { Select } from '../../components/Select';
import { api, type PipelineBudget } from '../../lib/api';
import { useStore } from '../../store/StoreProvider';
import { SettingsBlock } from './SettingsBlock';

// Impostazioni › Limiti dei modelli › Consumo per filiera (0.2.23, consumo di token): quanti passi
// può fare un lavoro e un controllo, e quanti giri di correzione. Ogni passo rimanda tutto il
// contesto: le chiamate lunghe sono quelle che consumano di più.
const STEPS = [4, 6, 8, 10, 12, 16, 20, 24, 30];
const ROUNDS = [0, 1, 2, 3, 4, 5];
// Il consumo è una STIMA dal numero di chiamate di un piano tipico (6 lavori in 2 fasi, senza
// correzioni): 12 chiamate per pezzo, 8 a fine fase, 7 solo alla fine. Non è una misura.
const MODES: { value: PipelineBudget['review_mode']; label: string }[] = [
  { value: 'piece', label: 'Controllo dopo ogni pezzo · 100% (stima)' },
  { value: 'phase', label: 'Controllo a fine fase · circa 65% (stima)' },
  { value: 'end', label: 'Un solo controllo alla fine · circa 60% (stima)' },
];
type Key = 'steps_per_task' | 'steps_per_review' | 'correction_rounds' | 'review_mode';

export function BudgetBlock() {
  const { toast } = useStore();
  const [rows, setRows] = useState<PipelineBudget[] | null>(null);
  useEffect(() => {
    api
      .pipelineBudgets()
      .then(setRows)
      .catch(() => setRows(null));
  }, []);
  if (!rows) return null;

  const change = (id: string, key: Key, value: number | string) => {
    const next = rows.map((r) => (r.id === id ? { ...r, [key]: value } : r));
    setRows(next);
    const body = Object.fromEntries(
      next.map((r) => [
        r.id,
        {
          steps_per_task: r.steps_per_task,
          steps_per_review: r.steps_per_review,
          correction_rounds: r.correction_rounds,
          review_mode: r.review_mode,
        },
      ]),
    );
    api
      .setPipelineBudgets(body)
      .then(setRows)
      .catch((e: Error) => toast({ kind: 'danger', title: 'Non salvato', body: e.message }));
  };
  const options = (list: number[], unit: (n: number) => string) =>
    list.map((n) => ({ value: String(n), label: unit(n) }));

  return (
    <SettingsBlock
      over="Consumo per filiera"
      title="Quanto può lavorare un pop in una chiamata"
      hint="Ogni passo rimanda al modello tutto il contesto: le chiamate lunghe sono quelle che consumano di più. Al tetto il pop consegna quello che ha. Le percentuali sono stime rispetto al controllo dopo ogni pezzo, non misure."
    >
      <div style={{ display: 'grid', gap: 10 }}>
        {rows.map((r) => (
          <div key={r.id} className="limits-row">
            <div className="limits-from">
              <span className="t-strong">{r.name}</span>
              <span className="t-caption dim">filiera</span>
            </div>
            <div className="limits-budget">
              <Select
                label={`Dove vanno i controlli, ${r.name}`}
                value={r.review_mode}
                options={MODES}
                onChange={(v) => change(r.id, 'review_mode', v)}
                width="100%"
              />
              <Select
                label={`Passi per lavoro, ${r.name}`}
                value={String(r.steps_per_task)}
                options={options(STEPS, (n) => `${n} passi per lavoro`)}
                onChange={(v) => change(r.id, 'steps_per_task', Number(v))}
                width="100%"
              />
              <Select
                label={`Passi per controllo, ${r.name}`}
                value={String(r.steps_per_review)}
                options={options(STEPS, (n) => `${n} passi per controllo`)}
                onChange={(v) => change(r.id, 'steps_per_review', Number(v))}
                width="100%"
              />
              <Select
                label={`Giri di correzione, ${r.name}`}
                value={String(r.correction_rounds)}
                options={options(ROUNDS, (n) =>
                  n === 0
                    ? 'Nessuna correzione'
                    : n === 1
                      ? '1 giro di correzione'
                      : `${n} giri di correzione`,
                )}
                onChange={(v) => change(r.id, 'correction_rounds', Number(v))}
                width="100%"
              />
            </div>
          </div>
        ))}
      </div>
    </SettingsBlock>
  );
}
